import { Column, Row } from "@hightouch/htui";
import { format, parseISO } from "date-fns";
import { noop } from "lodash";
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Legend } from "src/components/analytics/shared/legend";
import { renderCartesianGrid } from "src/components/charts/chart-grid";
import {
  chartSeriesStrokeDasharray,
  getTooltipCursorStyles,
  getXAxisStyles,
  getYAxisStyles,
} from "src/components/charts/chart-utils";
import {
  ChartTooltipColor,
  ChartTooltipTitle,
  ChartTooltipValue,
  ChartTooltipWrapper,
} from "src/components/charts/chart-tooltip";
import { CHART_COLORS } from "src/ui/colors";

interface FatigueCurvePoint {
  date: string;
  ctr: number | null;
  frequency: number | null;
}

interface FatigueCurveChartProps {
  data: FatigueCurvePoint[];
  labels: { ctr: string; frequency: string };
  height?: number;
}

const CTR_COLOR = CHART_COLORS.green.base;
const FREQUENCY_COLOR = CHART_COLORS.red.base;

const formatDateLabel = (date: string) => format(parseISO(date), "LLL d");
const formatCtr = (value: number) => `${value.toFixed(3)}%`;
const formatFrequency = (value: number) => `${value.toFixed(2)}x`;

export function FatigueCurveChart({
  data,
  labels,
  height = 200,
}: FatigueCurveChartProps) {
  if (data.length === 0) return null;

  return (
    <Column>
      <Legend
        series={[
          { key: "ctr", legendName: labels.ctr, color: CTR_COLOR },
          {
            key: "frequency",
            legendName: labels.frequency,
            color: FREQUENCY_COLOR,
          },
        ]}
        onHover={noop}
      />
      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          data={data}
          margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
        >
          {renderCartesianGrid({ vertical: true })}
          <XAxis
            {...getXAxisStyles({ tickAlign: "center" })}
            dataKey="date"
            interval={Math.max(0, Math.floor(data.length / 7))}
            tickFormatter={formatDateLabel}
          />
          <YAxis
            {...getYAxisStyles({ orientation: "left" })}
            yAxisId="ctr"
            width={45}
            tickFormatter={(value: number) => `${value.toFixed(1)}%`}
          />
          <YAxis
            {...getYAxisStyles()}
            yAxisId="freq"
            width={48}
            domain={[1, "auto"]}
            tickFormatter={(value: number) => `${value.toFixed(2)}x`}
          />
          <Tooltip
            cursor={getTooltipCursorStyles()}
            content={({ active, payload }) => {
              const point = payload?.[0]?.payload as
                | FatigueCurvePoint
                | undefined;
              if (!active || !point) return null;
              return (
                <ChartTooltipWrapper>
                  <ChartTooltipTitle>
                    {formatDateLabel(point.date)}
                  </ChartTooltipTitle>
                  {point.ctr !== null && (
                    <Row gap={2}>
                      <ChartTooltipColor color={CTR_COLOR} />
                      <ChartTooltipValue
                        value={{
                          value: formatCtr(point.ctr),
                          description: labels.ctr,
                        }}
                      />
                    </Row>
                  )}
                  {point.frequency !== null && (
                    <Row gap={2}>
                      <ChartTooltipColor color={FREQUENCY_COLOR} />
                      <ChartTooltipValue
                        value={{
                          value: formatFrequency(point.frequency),
                          description: labels.frequency,
                        }}
                      />
                    </Row>
                  )}
                </ChartTooltipWrapper>
              );
            }}
          />
          <Line
            yAxisId="ctr"
            type="monotone"
            dataKey="ctr"
            stroke={CTR_COLOR}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 6 }}
            connectNulls
            isAnimationActive={false}
          />
          <Line
            yAxisId="freq"
            type="monotone"
            dataKey="frequency"
            stroke={FREQUENCY_COLOR}
            strokeWidth={2}
            strokeDasharray={chartSeriesStrokeDasharray}
            dot={false}
            activeDot={{ r: 6 }}
            connectNulls
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Column>
  );
}
